"use client";

import { motion, AnimatePresence } from "framer-motion";
import { Globe, Check } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { SUPPORTED_LANGUAGES } from "../../lib/utils/language";

export const LanguageDropdown = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const lang =
      SUPPORTED_LANGUAGES.find((lang) => lang.code === i18n.language) ||
      SUPPORTED_LANGUAGES[0];
    setCurrentLanguage(lang);
  }, [i18n.language]);

  const changeLanguage = (languageCode) => {
    i18n.changeLanguage(languageCode);
    setIsOpen(false);
  };

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  if (!currentLanguage) {
    return null;
  }

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg text-[#a4a4a4] hover:text-[#f8f7f4] transition-colors duration-200 cursor-pointer"
      >
        <Globe className="w-4 h-4" />
        <span className="text-[14px] leading-[15px] tracking-[-0.03px] font-semibold">
          {currentLanguage.flag} {currentLanguage.nativeName}
        </span>
      </button>

      {/* Dropdown Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="absolute bottom-full mb-2 right-0 w-56 rounded-xl border border-[rgba(96,96,96,0.3)] overflow-hidden z-[9999]"
            style={{
              background: "rgba(30, 30, 30, 0.95)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              boxShadow:
                "0px 8px 32px rgba(0, 0, 0, 0.4), 0px 0px 0px 1px rgba(96, 96, 96, 0.1)",
            }}
          >
            {/* Scrollable List */}
            <div
              className="max-h-72 overflow-y-auto py-1"
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(164, 164, 164, 0.3) transparent",
              }}
            >
              {SUPPORTED_LANGUAGES.map((language) => {
                const isSelected = currentLanguage.code === language.code;
                const isEnabled = language.code === "en";
                return (
                  <button
                    key={language.code}
                    onClick={() => isEnabled && changeLanguage(language.code)}
                    className={`w-full flex items-center justify-between px-4 py-2.5 text-left transition-colors duration-150 ${
                      isEnabled ? "cursor-pointer" : "cursor-default"
                    } ${
                      isSelected
                        ? "bg-[rgba(187,225,230,0.08)] text-[#f8f7f4]"
                        : isEnabled
                        ? "text-[#a4a4a4] hover:bg-[rgba(255,255,255,0.04)] hover:text-[#f8f7f4]"
                        : "text-[#a4a4a4]/40"
                    }`}
                  >
                    <div className={`flex items-center gap-3 ${!isEnabled ? "opacity-40" : ""}`}>
                      <span className="text-base leading-none">
                        {language.flag}
                      </span>
                      <div className="flex flex-col">
                        <span className="text-[13px] font-medium leading-5">
                          {language.nativeName}
                        </span>
                        <span className="text-[11px] leading-4 opacity-60">
                          {language.name}
                        </span>
                      </div>
                    </div>
                    {isSelected && (
                      <Check className="w-3.5 h-3.5 text-[#bbe1e6] shrink-0" />
                    )}
                    {!isEnabled && (
                      <span className="text-[11px] font-medium text-[#909090] tracking-wide">
                        soon
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
